import React, { useContext } from 'react';
import invariant from 'tiny-invariant';
import PropTypes from 'prop-types';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import I18nContext from '../../contexts/i18n';
import SeoLink from '../common/seo_link';
import L from '../common/localized';

import './button.scss';

export function getAllUIButtonColors() {
  return [
    'black',
    'dark-green',
    'facebook-blue',
    'dark-shade-cyan',
    'gray',
    'purple-pizzazz',
    'red',
    'ribbon-blue',
    'turquoise',
    'lemon-gold',
    'pure-white',
    'light-blue',
    'neon-yellow'
  ];
};

const _colorToClassName = color => {
  // This name was incorrectly formatted as camelCase in the past.
  if (color === 'darkGreen') return 'dark-green';
  return color;
};

const UIButton = (props) => {
  const i18nSettings = useContext(I18nContext);
  const locale = i18nSettings.getLocale();
  return <UIButtonImpl locale={locale} {...props} />;
};

class UIButtonImpl extends React.Component {
  constructor(props) {
    super(props);
    this._onClick = this.onClick.bind(this);
  }

  onClick() {
    if (this.props.disabled) return;
    // SeoLinks may not need an onClick at all.
    if (this.props.onClick) {
      this.props.onClick();
    }
  }

  render() {
    invariant(!this.props.width || !this.props.fluid, 'Cannot specify a width & fluid at the same time.');
    invariant(this.props.content || this.props.icon, 'You must specify `content` or an `icon` in <UIButton>');

    const divStyle = {};
    if (this.props.width) {
      divStyle.width = this.props.width;
    } else if (this.props.fluid) {
      divStyle.width = '100%';
    }
    if (this.props.fontWeight) {
      divStyle.fontWeight = this.props.fontWeight;
    }
    if (!this.props.to && this.props.variantButton) divStyle.borderRadius = '100px';

    const combinedStyle = { ...divStyle, ...this.props.style };

    let classNames = 'lf-nice-button';
    if (this.props.color && this.props.color !== 'black') {
      classNames += ` ${_colorToClassName(this.props.color)}`;
    }

    if (this.props.solid) classNames += ' solid';
    if (this.props.boxy) classNames += ' boxy';
    if (this.props.taller) classNames += ' taller';
    if (this.props.disabled) classNames += ' disabled';
    if (this.props.noMargin) classNames += ' no-margin';
    if (this.props.big) classNames += ' big';
    if (this.props.letterSpacing) classNames += ' spaced-letters';
    if (this.props.noWrap) classNames += ' no-wrap';
    if (this.props.darkerBorder) classNames += ' darker-border';
    if (this.props.noBorder) classNames += ' no-border';
    if (this.props.noHover) classNames += ' no-hover';
    if (this.props.extraPadding) classNames += ' extra-padding';
    if (this.props.responsive) classNames += ' responsive';

    if (this.props.locale.startsWith('ar')) {
      classNames += ' regular-weight';
    }

    // In case there is an icon, we need to force the button to use row flexing.
    if (this.props.icon) classNames += ' flex-direction-row';
    if (!this.props.to && this.props.variantButton) combinedStyle.borderRadius = '100px';
    if (this.props.to) {
      return (
        <SeoLink to={this.props.to} style={{ ...combinedStyle, display: 'inline-block' }} onClick={this._onClick}>
          <div className={classNames} style={this.props.variantButton ? { borderRadius: '100px' } : {}}>
            {this.props.icon && <Icon name={this.props.icon} />}
            {typeof this.props.content === 'string'
              ? <L text={this.props.content} isToken={this.props.isToken} />
              : this.props.content}
          </div>
        </SeoLink>
      );
    }

    return (
      // eslint-disable-next-line react/button-has-type
      <button type={this.props.submit ? 'submit' : 'button'} className={classNames} onClick={this._onClick}
        style={combinedStyle}>
        {this.props.icon && <Icon name={this.props.icon} />}
        {typeof this.props.content === 'string'
          ? <L text={this.props.content} isToken={this.props.isToken} />
          : this.props.content}
      </button>
    );
  }
}

if (process.env.NODE_ENV !== 'production') {
  UIButton.propTypes = {
    onClick: PropTypes.func,
    style: PropTypes.object,
    icon: PropTypes.string,
    content: PropTypes.any.isRequired,
    submit: PropTypes.bool,
    to: PropTypes.string,
    width: PropTypes.string,
    fluid: PropTypes.bool,
    disabled: PropTypes.bool,
    big: PropTypes.bool,
    boxy: PropTypes.bool,
    color: PropTypes.oneOf(getAllUIButtonColors()),
    solid: PropTypes.bool,
    noMargin: PropTypes.bool,
    letterSpacing: PropTypes.bool,
    noWrap: PropTypes.bool,
    darkerBorder: PropTypes.bool,
    noBorder: PropTypes.bool,
    extraPadding: PropTypes.bool,
    responsive: PropTypes.bool
  };
}

export default UIButton;
